import { tideDay, nextTide, type TideDay } from './sources/tides';
import { feet, levelAt, renderTideDay, renderTideTable, tideTrend } from './tideChart';
import type { TideStation } from './tideStations';

const timeFmt = new Intl.DateTimeFormat([], { hour: 'numeric', minute: '2-digit' });

/**
 * Detail panel for a tide station: the whole day's predicted curve, its highs
 * and lows, and where the water sits right now.
 *
 * Tide stations carry no wind, so this is a separate view from the station
 * panel rather than a variant of it — and it says "predicted" plainly, because
 * unlike the wind none of this is measured.
 */
function nowBlock(day: TideDay, now: Date): string {
  const level = levelAt(day.curve, now);
  const trend = tideTrend(day, now);
  const next = nextTide(day.extremes, now);

  if (level === null) return '';

  return `
    <div class="reading-row">
      <div class="reading-main">
        <span class="reading-value">${feet(level)}</span>
        <span class="reading-unit">ft</span>
      </div>
      <dl class="reading-meta">
        <div><dt>Now</dt><dd>${trend ?? '—'}</dd></div>
        ${next
          ? `<div><dt>Next ${next.kind}</dt>` +
            `<dd>${timeFmt.format(next.t)} <span class="deg">${feet(next.heightFt)} ft</span></dd></div>`
          : ''}
      </dl>
    </div>`;
}

export function buildTidePopup(station: TideStation, onClose: () => void): HTMLElement {
  const root = document.createElement('div');
  root.className = 'popup';

  root.innerHTML = `
    <header class="popup-head">
      <button type="button" class="popup-close" aria-label="Close station detail">×</button>
      <h2>${station.name}</h2>
      <p class="popup-sub">
        <span class="badge badge-tide">Tide station</span>
        <span class="muted">predicted, not measured</span>
      </p>
    </header>
    <div class="now-slot"></div>
    <div class="chart-head">
      <span class="chart-range">Next 33 hours · ft above MLLW</span>
      <div class="view-toggle" role="group" aria-label="Chart or table view">
        <button type="button" data-view="chart" aria-pressed="true">Chart</button>
        <button type="button" data-view="table" aria-pressed="false">Table</button>
      </div>
    </div>
    <div class="chart-host"><p class="chart-empty">Loading tide prediction…</p></div>
    <a class="source-link"
       href="https://tidesandcurrents.noaa.gov/stationhome.html?id=${station.id}"
       target="_blank" rel="noopener">NOAA station page ↗</a>`;

  root.querySelector<HTMLButtonElement>('.popup-close')!.addEventListener('click', onClose);

  const host = root.querySelector<HTMLElement>('.chart-host')!;
  const buttons = root.querySelectorAll<HTMLButtonElement>('.view-toggle button');

  let day: TideDay | null = null;
  let view: 'chart' | 'table' = 'chart';

  const draw = () => {
    if (!day) return;
    if (view === 'chart') renderTideDay(host, day);
    else renderTideTable(host, day);
  };

  buttons.forEach((btn) => {
    btn.addEventListener('click', () => {
      view = btn.dataset.view as 'chart' | 'table';
      buttons.forEach((b) => b.setAttribute('aria-pressed', String(b === btn)));
      draw();
    });
  });

  tideDay(station.id)
    .then((loaded) => {
      day = loaded;
      root.querySelector<HTMLElement>('.now-slot')!.outerHTML = nowBlock(loaded, new Date());
      // Two thirds of CO-OPS stations are subordinate: NOAA publishes their
      // highs and lows but has no constituents to draw the shape between. Say
      // so, rather than let an interpolation pass for a harmonic prediction.
      if (loaded.derived) {
        const note = root.querySelector<HTMLElement>('.popup-sub .muted');
        if (note) note.textContent = 'predicted, not measured · shape between turns interpolated';
      }
      draw();
    })
    .catch((err) => {
      host.innerHTML = `<p class="chart-empty">Could not load tide prediction: ${
        err instanceof Error ? err.message : String(err)
      }</p>`;
    });

  return root;
}
